import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useIntlLocale, useLocations, useTranslation } from '@hooks';
import { EVENT_FIELD_OPTIONS, PAYMENT_PROVIDERS } from '@constants/automations';
import { getActiveIntegrations, isIntegrationConnected } from '@helpers/integrations';
import { SEX_LABEL_KEYS, SEX_NAMES, languageOptions } from '@helpers/contact-demographics';

export const useEventFieldOptions = () => {
    const { t } = useTranslation();
    const intlLocale = useIntlLocale();
    const account = useSelector(state => state.account?.account);
    const { locationsMap } = useLocations();

    return useMemo(
        () => ({
            [EVENT_FIELD_OPTIONS.PROVIDERS]: PAYMENT_PROVIDERS.filter(provider =>
                isIntegrationConnected(account, provider.integration),
            ).map(({ value, label }) => ({ value, label })),
            [EVENT_FIELD_OPTIONS.ACCOUNTS]: PAYMENT_PROVIDERS.flatMap(provider =>
                getActiveIntegrations(account, provider.integration).map(integration => ({
                    value: integration.userIdentifier,
                    label: integration.accountLabel
                        ? `${provider.label} · ${integration.accountLabel}`
                        : provider.label,
                })),
            ),
            [EVENT_FIELD_OPTIONS.LOCATIONS]: Object.values(locationsMap).map(location => ({
                value: String(location.companyId),
                label: location.internalName,
            })),
            [EVENT_FIELD_OPTIONS.SEXES]: Object.entries(SEX_NAMES).map(([name, value]) => ({
                value: name,
                label: t(SEX_LABEL_KEYS[value]),
            })),
            [EVENT_FIELD_OPTIONS.LANGUAGES]: languageOptions(intlLocale),
        }),
        [account, locationsMap, intlLocale, t],
    );
};
